import React, { useState, useEffect, useRef } from "react";
import {
    Container, Row, Col, Form, Button, InputGroup, Spinner
} from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import logo from "../Images/AZ.png";

function LoginPage() {
    const [credentials, setCredentials] = useState({ user_name: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [currentTrustMessage, setCurrentTrustMessage] = useState(0);
    const [graphData, setGraphData] = useState({
        sales: 0,
        revenue: 0,
        customers: 0,
        orders: 0,
        growth: 0,
        active: 0
    });
    const containerRef = useRef(null);
    const navigate = useNavigate();

    const progress = credentials.user_name.trim() ?
        (credentials.password.trim() ? 100 : 50) : 0;

    const trustMessages = [
        "Built for secure, high-performance dashboards",
        "Optimized for real-time data processing",
        "Role-based access with strict permission control",
        "Designed for enterprise-scale workloads",
        "High availability with fault-tolerant architecture",
        "Secure data handling across all modules",
        "Consistent performance under heavy usage",
        "Audit-ready with detailed activity tracking",
        "Scalable architecture for growing operations",
        "Reliable session and access management",
        "Optimized UI for fast operational workflows",
        "Structured data flow for accurate reporting",
        "System stability ensured through controlled releases",
        "Built to support multi-user concurrent access",
        "Performance-tuned for analytics-heavy dashboards"
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.4
            }
        }
    };

    const childVariants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.9
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 120,
                damping: 15
            }
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTrustMessage((prev) => (prev + 1) % trustMessages.length);
            setGraphData({
                sales: Math.floor(Math.random() * 1500) + 500,
                revenue: Math.floor(Math.random() * 25000) + 10000,
                customers: Math.floor(Math.random() * 800) + 200,
                orders: Math.floor(Math.random() * 1200) + 300,
                growth: Math.floor(Math.random() * 25) + 5,
                active: Math.floor(Math.random() * 450) + 150
            });
        }, 3000);
        return () => clearInterval(interval);
    }, [trustMessages.length]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleMouseMove = (e) => {
            const rect = container.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width * 200 - 100;
            const y = (e.clientY - rect.top) / rect.height * 200 - 100;
            container.style.transform = `perspective(1000px) rotateX(${-y / 8}deg) rotateY(${x / 8}deg)`;
        };

        const handleMouseLeave = () => {
            container.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
        };

        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            container.removeEventListener('mousemove', handleMouseMove);
            container.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setInitialLoading(false), 2500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await axios.get("https://www.azzenterprises.com/api/admin/getuser", { withCredentials: true });
                if (res.data.user_name) navigate("/customers");
            } catch { }
        };
        checkAuth();
    }, [navigate]);

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const handleChange = (e) => setCredentials({ ...credentials, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post(
                "https://www.azzenterprises.com/api/admin/login",
                credentials,
                { withCredentials: true }
            );
            const user = response.data?.user;

            if (user?.status === "inactive") {
                toast.error("Your account is inactive. Contact admin.");
                setLoading(false);
                return;
            }

            toast.success("Login successful!");
            localStorage.setItem("user_name", user?.user_name || credentials.user_name);
            localStorage.setItem("user_role", user?.user_role || "user");

            setTimeout(() => {
                setLoading(false);
                navigate("/customers");
            }, 1500);
        } catch (error) {
            toast.error(error.response?.data?.message || "Invalid username or password.");
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="position-relative min-vh-100 overflow-hidden d-flex align-items-center justify-content-center">
                <motion.div
                    className="position-absolute top-0 start-0 w-100 h-100"
                    initial={{ scale: 1.3 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2 }}
                    style={{
                        background:
                            "radial-gradient(circle at 30% 70%, #667eea 0%, transparent 50%), radial-gradient(circle at 70% 30%, #764ba2 0%, transparent 50%)"
                    }}
                />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    className="position-relative z-10"
                >
                    <div className="d-flex gap-2">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <motion.span
                                key={i}
                                style={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: "#fff"
                                }}
                                animate={{
                                    y: [0, -12, 0],
                                    opacity: [0.6, 1, 0.6]
                                }}
                                transition={{
                                    duration: 0.6,
                                    repeat: Infinity,
                                    delay: i * 0.15,
                                    ease: "easeInOut"
                                }}
                            />
                        ))}
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="position-relative min-vh-100 overflow-hidden d-flex align-items-center justify-content-center py-5" style={{
            background: "linear-gradient(135deg, #0a0a1a 0%, #1a0033 50%, #000 100%)",
            minHeight: "70vh",
            fontSize: "clamp(13px, 1.1vw, 15px)"
        }}>
            <div className="position-absolute inset-0 d-flex flex-wrap align-content-between p-4 w-100">
                {['Sales', 'Revenue', 'Customers', 'Orders', 'Growth', 'Active'].map((metric, i) => (
                    <div key={metric} className="col-6 col-md-4 col-lg-2 p-2 flex-grow-1 min-h-0" style={{ opacity: 0.12 }}>
                        <div className="d-flex flex-column h-100 bg-dark bg-opacity-30 rounded-3 p-3 position-relative overflow-hidden"
                            style={{ backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <div className="text-white small mb-2 opacity-85 fw-semibold">
                                {metric}
                            </div>
                            <div className="flex-grow-1 position-relative mb-2">
                                {i === 0 && (
                                    <div className="d-flex flex-column h-100 gap-1">
                                        {[0.3, 0.7, 0.4, 0.9, 0.6].map((height, j) => (
                                            <motion.div
                                                key={j}
                                                className="flex-fill rounded bg-gradient"
                                                style={{
                                                    height: `${height * 80}%`,
                                                    background: `linear-gradient(180deg, hsl(${220 + j * 15}, 70%, 50%) 0%, hsl(${220 + j * 15}, 70%, 30%) 100%)`,
                                                    borderRadius: '4px'
                                                }}
                                                animate={{
                                                    height: [`${height * 80}%`, `${(height + Math.sin(Date.now() / 1000 + j) * 0.1) * 80}%`],
                                                    scaleY: [1, 1.1, 1]
                                                }}
                                                transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                                            />
                                        ))}
                                    </div>
                                )}
                                {i === 1 && (
                                    <svg viewBox="0 0 100 60" className="w-100 h-100">
                                        <motion.path
                                            d="M10 50 Q25 30 40 20 55 35 70 25 85 40 90 35"
                                            fill="none"
                                            stroke="hsl(160, 70%, 50%)"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            animate={{
                                                d: [
                                                    "M10 50 Q25 45 40 48 55 50 70 49 85 50 90 50",
                                                    "M10 50 Q25 30 40 20 55 35 70 25 85 40 90 35",
                                                    "M10 50 Q25 25 40 15 55 30 70 20 85 35 90 30"
                                                ]
                                            }}
                                            transition={{ duration: 4, repeat: Infinity, repeatType: "loop" }}
                                        />
                                        <circle cx="10" cy="50" r="3" fill="hsl(160, 70%, 50%)" className="animate-pulse" />
                                        <circle cx="90" cy="35" r="3" fill="hsl(160, 70%, 50%)" className="animate-pulse" />
                                    </svg>
                                )}
                                {i === 2 && (
                                    <svg viewBox="0 0 80 80" className="w-100 h-100">
                                        <motion.circle
                                            cx="40" cy="40" r="30"
                                            fill="none"
                                            stroke="hsl(30, 70%, 50%)"
                                            strokeWidth="6"
                                            strokeLinecap="round"
                                            strokeDasharray="188"
                                            strokeDashoffset={188 * 0.7}
                                            pathLength={0.7}
                                            animate={{ pathLength: [0.7, 0.8, 0.7], rotate: [0, 360] }}
                                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        />
                                        <motion.circle
                                            cx="40" cy="40" r="30"
                                            fill="none"
                                            stroke="hsl(200, 70%, 50%)"
                                            strokeWidth="6"
                                            strokeLinecap="round"
                                            strokeDasharray="188"
                                            strokeDashoffset={0}
                                            pathLength={0.3}
                                            animate={{ pathLength: [0.3, 0.4, 0.3], rotate: [0, -360] }}
                                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        />
                                    </svg>
                                )}
                                {i === 3 && (
                                    <div className="position-relative h-100 w-100">
                                        <motion.div
                                            className="position-absolute bottom-0 start-0 end-0 rounded"
                                            style={{
                                                height: '60%',
                                                background: 'linear-gradient(90deg, hsl(340, 70%, 50%) 0%, hsl(340, 70%, 30%) 100%)',
                                                borderRadius: '4px 4px 0 0'
                                            }}
                                            animate={{
                                                height: ['60%', '75%', '65%', '80%', '60%'],
                                                scaleX: [1, 1.02, 1]
                                            }}
                                            transition={{ duration: 3, repeat: Infinity, repeatType: "loop" }}
                                        />
                                    </div>
                                )}
                                {i === 4 && (
                                    <svg viewBox="0 0 140 80" className="w-100 h-100">
                                        <defs>
                                            <linearGradient id="barGrad1" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" stopColor="hsl(200, 70%, 55%)">
                                                    <animate attributeName="stop-color" values="hsl(200, 70%, 55%);hsl(210, 70%, 60%);hsl(200, 70%, 55%)" dur="2s" repeatCount="indefinite" />
                                                </stop>
                                                <stop offset="100%" stopColor="hsl(200, 70%, 25%)">
                                                    <animate attributeName="stop-color" values="hsl(200, 70%, 25%);hsl(210, 70%, 30%);hsl(200, 70%, 25%)" dur="2s" repeatCount="indefinite" />
                                                </stop>
                                            </linearGradient>
                                            <linearGradient id="barGrad2" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" stopColor="hsl(160, 70%, 55%)">
                                                    <animate attributeName="stop-color" values="hsl(160, 70%, 55%);hsl(170, 70%, 60%);hsl(160, 70%, 55%)" dur="2s" repeatCount="indefinite" />
                                                </stop>
                                                <stop offset="100%" stopColor="hsl(160, 70%, 25%)">
                                                    <animate attributeName="stop-color" values="hsl(160, 70%, 25%);hsl(170, 70%, 30%);hsl(160, 70%, 25%)" dur="2s" repeatCount="indefinite" />
                                                </stop>
                                            </linearGradient>
                                            <linearGradient id="barGrad3" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" stopColor="hsl(180, 70%, 55%)">
                                                    <animate attributeName="stop-color" values="hsl(180, 70%, 55%);hsl(190, 70%, 60%);hsl(180, 70%, 55%)" dur="2s" repeatCount="indefinite" />
                                                </stop>
                                                <stop offset="100%" stopColor="hsl(180, 70%, 25%)">
                                                    <animate attributeName="stop-color" values="hsl(180, 70%, 25%);hsl(190, 70%, 30%);hsl(180, 70%, 25%)" dur="2s" repeatCount="indefinite" />
                                                </stop>
                                            </linearGradient>
                                        </defs>

                                        <motion.g className="position-relative">
                                            {[
                                                { x: 15, height: 45, grad: "barGrad1", delay: 0 },
                                                { x: 35, height: 65, grad: "barGrad2", delay: 0.2 },
                                                { x: 55, height: 52, grad: "barGrad3", delay: 0.4 },
                                                { x: 75, height: 70, grad: "barGrad1", delay: 0.6 },
                                                { x: 95, height: 38, grad: "barGrad2", delay: 0.8 },
                                                { x: 115, height: 58, grad: "barGrad3", delay: 1 }
                                            ].map((bar, j) => (
                                                <motion.g key={j}>
                                                    <motion.rect
                                                        x={bar.x} y={80 - bar.height}
                                                        width="16" height={bar.height}
                                                        rx="3" ry="3"
                                                        fill={`url(#${bar.grad})`}
                                                        initial={{ height: 0, y: 80 }}
                                                        animate={{
                                                            height: [bar.height * 0.6, bar.height, bar.height * 0.9, bar.height],
                                                            y: [80, 80 - bar.height, 80 - bar.height * 0.9, 80 - bar.height],
                                                            scaleX: [0.8, 1, 1.05, 1]
                                                        }}
                                                        transition={{
                                                            duration: 2.5,
                                                            delay: bar.delay,
                                                            repeat: Infinity,
                                                            repeatType: "loop",
                                                            ease: "easeInOut"
                                                        }}
                                                    />
                                                    <motion.circle
                                                        cx={bar.x + 8} cy={80 - bar.height - 4}
                                                        r="3"
                                                        fill="rgba(255,255,255,0.8)"
                                                        animate={{
                                                            scale: [0, 1.4, 1],
                                                            opacity: [0.8, 1, 0.8]
                                                        }}
                                                        transition={{
                                                            duration: 1.5,
                                                            delay: bar.delay + 0.5,
                                                            repeat: Infinity,
                                                            repeatType: "reverse"
                                                        }}
                                                    />
                                                </motion.g>
                                            ))}

                                            <motion.line
                                                x1="15" y1="75" x2="115" y2="75"
                                                stroke="rgba(255,255,255,0.2)"
                                                strokeWidth="1"
                                                strokeDasharray="5,5"
                                                animate={{
                                                    strokeDashoffset: [-10, 10],
                                                    opacity: [0.3, 0.6, 0.3]
                                                }}
                                                transition={{ duration: 3, repeat: Infinity }}
                                            />
                                        </motion.g>
                                    </svg>
                                )}
                                {i === 5 && (
                                    <div className="position-relative h-100 w-100 d-flex align-items-center justify-content-center">
                                        <motion.div
                                            style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: 'hsl(120, 70%, 50%)'
                                            }}
                                            animate={{
                                                scale: [1, 1.3, 1],
                                                rotate: [0, 180, 360]
                                            }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        />
                                    </div>
                                )}
                            </div>
                            <motion.div
                                className="text-white mt-1 fw-bold small"
                                key={`${metric}-${graphData[Object.keys(graphData)[i % 6]]}`}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                            >
                                {graphData[Object.keys(graphData)[i % 6]]?.toLocaleString() || '1,234'}
                                {i === 1 && '$'}
                                {i === 4 && '%'}
                            </motion.div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="position-absolute inset-0">
                <motion.div
                    animate={{
                        background: [
                            "radial-gradient(circle at 20% 80%, #667eea30 0%, transparent 50%)",
                            "radial-gradient(circle at 80% 20%, #764ba230 0%, transparent 50%)",
                        ]
                    }}
                    transition={{ duration: 5, repeat: Infinity }}
                />
                {[...Array(6)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="position-absolute rounded-full"
                        style={{
                            width: `clamp(50px, ${6 + i * 3}vw, 100px)`,
                            height: `clamp(50px, ${6 + i * 3}vw, 100px)`,
                            background: `radial-gradient(circle, hsl(${180 + i * 30}, 60%, 25%) 0%, transparent 70%)`,
                            left: `${8 + i * 14}%`,
                            top: `${15 + i * 13}%`
                        }}
                        animate={{
                            y: [-25, 25, -25],
                            scale: [1, 1.15, 1],
                            rotate: [0, 180, 360]
                        }}
                        transition={{ duration: 12 + i * 2, repeat: Infinity }}
                    />
                ))}
            </div>

            <Container className="h-100 px-3 position-relative z-20" style={{ zIndex: 20 }}>
                <Row className="justify-content-center align-items-center h-100 gy-3">
                    <Col xs={12} sm={11} md={10} lg={8} xl={6}>
                        <motion.div
                            ref={containerRef}
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="parallax-container mx-auto"
                            style={{
                                transformStyle: "preserve-3d",
                                maxWidth: "450px",
                                margin: "0 auto"
                            }}
                        >
                            <motion.div
                                variants={childVariants}
                                className="text-center mb-1 position-relative"
                                style={{ transform: "translateZ(30px)" }}
                            >
                                <motion.img
                                    src={logo}
                                    alt="AZZ Enterprises"
                                    style={{
                                        width: "clamp(50px, 10vw, 80px)",
                                        height: "clamp(50px, 10vw, 80px)",
                                        filter: "drop-shadow(0 10px 25px rgba(102,126,234,0.5))"
                                    }}
                                    drag
                                    dragElastic={0.12}
                                    dragConstraints={{ top: -8, left: -8, right: 8, bottom: 8 }}
                                    whileHover={{ scale: 1.1 }}
                                />
                                <motion.h2
                                    className="fw-bold mt-2 mb-1"
                                    style={{
                                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        fontSize: "clamp(1.4rem, 4vw, 2.2rem)"
                                    }}
                                >
                                    Secure Access
                                </motion.h2>
                            </motion.div>

                            <motion.div
                                variants={childVariants}
                                className="text-center mb-4 position-relative"
                                style={{ transform: "translateZ(15px)" }}
                            >
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentTrustMessage}
                                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                        transition={{ duration: 0.5, ease: "easeInOut" }}
                                        style={{
                                            color: "#a0c4ff",
                                            fontSize: "clamp(0.75rem, 1.2vw, 0.9rem)",
                                            fontWeight: 500,
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {trustMessages[currentTrustMessage]}
                                    </motion.div>
                                </AnimatePresence>
                            </motion.div>

                            <Form onSubmit={handleSubmit}>
                                <motion.div variants={childVariants} className="mb-3" style={{ transform: "translateZ(20px)" }}>
                                    <Form.Label className="fw-semibold mb-1" style={{
                                        color: "#e0e0ff",
                                        fontSize: "clamp(0.85rem, 1.8vw, 1rem)",
                                        marginLeft: "4px"
                                    }}>
                                        Username
                                    </Form.Label>
                                    <motion.div whileHover={{ scale: 1.015 }} whileFocus={{ scale: 1.03 }}>
                                        <Form.Control
                                            type="text"
                                            name="user_name"
                                            value={credentials.user_name}
                                            onChange={handleChange}
                                            required
                                            style={{
                                                background: "linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.04))",
                                                backdropFilter: "blur(15px)",
                                                border: "1px solid rgba(255,255,255,0.15)",
                                                borderRadius: "12px",
                                                padding: "clamp(12px, 2vw, 14px) clamp(16px, 3vw, 20px)",
                                                color: "#ffffff",
                                                fontSize: "clamp(0.9rem, 1.8vw, 1.05rem)",
                                                boxShadow: "0 8px 25px rgba(0,0,0,0.25)"
                                            }}
                                        />
                                    </motion.div>
                                </motion.div>

                                <motion.div variants={childVariants} className="mb-4" style={{ transform: "translateZ(20px)" }}>
                                    <Form.Label className="fw-semibold mb-1" style={{
                                        color: "#e0e0ff",
                                        fontSize: "clamp(0.85rem, 1.8vw, 1rem)",
                                        marginLeft: "4px"
                                    }}>
                                        Password
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={credentials.password}
                                            onChange={handleChange}
                                            required
                                            style={{
                                                background: "linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.04))",
                                                backdropFilter: "blur(15px)",
                                                border: "1px solid rgba(255,255,255,0.15)",
                                                borderRadius: "12px 0 0 12px",
                                                padding: "clamp(12px, 2vw, 14px) clamp(16px, 3vw, 20px)",
                                                color: "#ffffff",
                                                fontSize: "clamp(0.9rem, 1.8vw, 1.05rem)"
                                            }}
                                        />
                                        <motion.button
                                            type="button"
                                            onClick={togglePasswordVisibility}
                                            style={{
                                                background: "rgba(255,255,255,0.12)",
                                                backdropFilter: "blur(15px)",
                                                border: "1px solid rgba(255,255,255,0.18)",
                                                borderRadius: "0 12px 12px 0",
                                                padding: "clamp(12px, 2vw, 14px) 16px",
                                                cursor: "pointer",
                                                minWidth: "48px"
                                            }}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <i className={`bi bi-eye${showPassword ? "-slash" : ""}`} style={{
                                                color: "#e0e0ff",
                                                fontSize: "clamp(0.95rem, 2vw, 1.1rem)"
                                            }} />
                                        </motion.button>
                                    </InputGroup>
                                </motion.div>

                                <motion.div variants={childVariants} style={{ transform: "translateZ(25px)" }}>
                                    <Button
                                        type="submit"
                                        className="w-100 position-relative border-0 rounded-2 fw-bold overflow-hidden"
                                        disabled={loading}
                                        style={{
                                            background: progress === 100
                                                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                                : progress === 50
                                                    ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                                    : "linear-gradient(135deg, rgba(102,126,234,0.4) 0%, rgba(118,75,162,0.4) 100%)",
                                            boxShadow: progress === 100
                                                ? "0 12px 35px rgba(102,126,234,0.5)"
                                                : "0 8px 20px rgba(102,126,234,0.2)",
                                            fontSize: "clamp(1rem, 2.2vw, 1.25rem)",
                                            fontWeight: "700",
                                            padding: "clamp(12px, 2.5vw, 16px) clamp(20px, 4vw, 28px)",
                                            minHeight: "52px",
                                            position: "relative"
                                        }}
                                        whileHover={!loading ? {
                                            scale: 1.03,
                                            boxShadow: progress === 100 ? "0 18px 45px rgba(102,126,234,0.7)" : "0 12px 30px rgba(102,126,234,0.4)",
                                            y: -2
                                        } : {}}
                                        whileTap={!loading ? { scale: 0.97 } : {}}
                                    >
                                        <div
                                            className="position-absolute inset-0 d-flex align-items-center justify-content-center"
                                            style={{
                                                background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) ${progress}%, transparent 100%)`,
                                                zIndex: 1,
                                                borderRadius: "inherit"
                                            }}
                                        />
                                        <AnimatePresence mode="wait">
                                            {loading ? (
                                                <motion.span
                                                    key="loading"
                                                    initial={{ scaleX: 0 }}
                                                    animate={{ scaleX: 1 }}
                                                    exit={{ scaleX: 0 }}
                                                    style={{
                                                        originX: 0,
                                                        zIndex: 2,
                                                        position: "relative"
                                                    }}
                                                    className="d-flex align-items-center justify-content-center"
                                                >
                                                    <Spinner animation="grow" size="sm" className="me-2" />
                                                    Launching...
                                                </motion.span>
                                            ) : progress === 100 ? (
                                                <motion.span
                                                    key="launch"
                                                    className="d-flex align-items-center justify-content-center position-relative z-10"
                                                >
                                                    Launch Dashboard
                                                </motion.span>
                                            ) : progress === 50 ? (
                                                <motion.span
                                                    key="password"
                                                    className="d-flex align-items-center justify-content-center position-relative z-10"
                                                >
                                                    Enter Password
                                                </motion.span>
                                            ) : (
                                                <motion.span
                                                    key="username"
                                                    className="d-flex align-items-center justify-content-center position-relative z-10"
                                                >
                                                    Enter Username
                                                </motion.span>
                                            )}
                                        </AnimatePresence>
                                    </Button>
                                </motion.div>
                            </Form>

                            <motion.div
                                variants={childVariants}
                                className="text-center mt-3 pt-2 opacity-70"
                                style={{
                                    color: "#b0b0dd",
                                    fontSize: "clamp(0.7rem, 1.5vw, 0.85rem)",
                                    borderTop: "1px solid rgba(255,255,255,0.08)"
                                }}
                            >
                                © 2026 AZZ ENTERPRISES PTE LTD
                            </motion.div>
                        </motion.div>
                    </Col>
                </Row>
            </Container>

            <style jsx global>{`
                .parallax-container * { will-change: transform; }
                .parallax-container {
                    perspective: 1200px;
                }
                @media (min-width: 1200px) {
                    .parallax-container {
                        max-width: 450px !important;
                    }
                }
                .bg-gradient {
                    background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
                }
            `}</style>

            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={true}
                newestOnTop
                closeOnClick
                rtl={false}
                theme="dark"
            />
        </div>
    );
}

export default LoginPage;
